import React, { useEffect, useRef } from 'react';
import { BRAND_COLORS } from './constants';

// Version 4: Enhanced Smart Tetris Background
export const TetrisBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Configuration state
        let config = {
            spawnRate: 3,
            maxPieces: 5,
            fallSpeed: 1,
            rotationEnabled: true,
            globalOpacity: 80,
            globalScale: 100
        };

        const blockSize = 20; // Same as V3 for consistency
        let cols = Math.floor(canvas.width / blockSize);
        let rows = Math.floor(canvas.height / blockSize);

        // Enhanced Tetris pieces with rotation states
        const tetrisPieces = {
            I: [
                [[1, 1, 1, 1]],
                [[1], [1], [1], [1]]
            ],
            O: [
                [[1, 1], [1, 1]]
            ],
            T: [
                [[0, 1, 0], [1, 1, 1]],
                [[1, 0], [1, 1], [1, 0]],
                [[1, 1, 1], [0, 1, 0]],
                [[0, 1], [1, 1], [0, 1]]
            ],
            L: [
                [[1, 0, 0], [1, 1, 1]],
                [[1, 1], [1, 0], [1, 0]],
                [[1, 1, 1], [0, 0, 1]],
                [[0, 1], [0, 1], [1, 1]]
            ],
            J: [
                [[0, 0, 1], [1, 1, 1]],
                [[1, 0], [1, 0], [1, 1]],
                [[1, 1, 1], [1, 0, 0]],
                [[1, 1], [0, 1], [0, 1]]
            ],
            S: [
                [[0, 1, 1], [1, 1, 0]],
                [[1, 0], [1, 1], [0, 1]]
            ],
            Z: [
                [[1, 1, 0], [0, 1, 1]],
                [[0, 1], [1, 1], [1, 0]]
            ]
        };

        interface SmartPiece {
            shape: number[][];
            x: number;
            y: number;
            color: string;
            rotationState: number;
            pieceType: keyof typeof tetrisPieces;
            fallSpeed: number;
            lastFallTime: number;
            lastMoveTime: number;
            targetX: number;
            targetRotation: number;
            isActive: boolean;
        }

        let activePieces: SmartPiece[] = [];
        let settledBlocks: Array<Array<string | null>> = [];
        let lastSpawnTime = 0;
        let gameOverHeight = 0;
        let isGameOver = false;

        const pieceColors = [
            `${BRAND_COLORS.primary}70`,
            `${BRAND_COLORS.secondary}70`,
            '#805AD570', // Purple
            '#38B2AC70', // Teal
            '#ED893670', // Orange
            '#E53E3E70', // Red
        ];

        // Listen for configuration changes
        const handleConfigChange = (event: CustomEvent) => {
            const newConfig = event.detail;
            config = {
                spawnRate: newConfig.v4SpawnRate || config.spawnRate,
                maxPieces: newConfig.v4MaxPieces || config.maxPieces,
                fallSpeed: newConfig.v4FallSpeed || config.fallSpeed,
                rotationEnabled: newConfig.v4RotationEnabled !== undefined ? newConfig.v4RotationEnabled : config.rotationEnabled,
                globalOpacity: newConfig.globalOpacity || config.globalOpacity,
                globalScale: newConfig.globalScale || config.globalScale
            };
        };

        window.addEventListener('backgroundConfigChange', handleConfigChange as EventListener);

        const initializeGame = () => {
            activePieces = [];
            settledBlocks = Array(rows).fill(null).map(() => Array(cols).fill(null));
            gameOverHeight = 0;
            isGameOver = false;
        };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            cols = Math.floor(canvas.width / blockSize);
            rows = Math.floor(canvas.height / blockSize);
            initializeGame();
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        const createSmartPiece = (): SmartPiece => {
            const pieceTypes = Object.keys(tetrisPieces) as Array<keyof typeof tetrisPieces>;
            const pieceType = pieceTypes[Math.floor(Math.random() * pieceTypes.length)];
            const rotations = tetrisPieces[pieceType];
            const startX = Math.floor(cols / 4) + Math.floor(Math.random() * (cols / 2));

            return {
                shape: rotations[0],
                x: startX,
                y: -2,
                color: pieceColors[Math.floor(Math.random() * pieceColors.length)],
                rotationState: 0,
                pieceType,
                fallSpeed: 300 / config.fallSpeed,
                lastFallTime: 0,
                lastMoveTime: 0,
                targetX: startX + Math.floor(Math.random() * 7) - 3, // Random target within reach
                targetRotation: config.rotationEnabled ? Math.floor(Math.random() * rotations.length) : 0,
                isActive: true
            };
        };

        const canPlacePiece = (piece: SmartPiece, newX: number, newY: number, shape?: number[][]): boolean => {
            const shapeToCheck = shape || piece.shape;

            for (let row = 0; row < shapeToCheck.length; row++) {
                for (let col = 0; col < shapeToCheck[row].length; col++) {
                    if (shapeToCheck[row][col]) {
                        const boardX = newX + col;
                        const boardY = newY + row;

                        // Check bounds
                        if (boardX < 0 || boardX >= cols || boardY >= rows) {
                            return false;
                        }

                        // Check collision with settled blocks (only if on board)
                        if (boardY >= 0 && settledBlocks[boardY][boardX]) {
                            return false;
                        }

                        // Check collision with other active pieces
                        for (let otherPiece of activePieces) {
                            if (otherPiece === piece || !otherPiece.isActive) continue;

                            for (let oRow = 0; oRow < otherPiece.shape.length; oRow++) {
                                for (let oCol = 0; oCol < otherPiece.shape[oRow].length; oCol++) {
                                    if (otherPiece.shape[oRow][oCol]) {
                                        const otherX = otherPiece.x + oCol;
                                        const otherY = otherPiece.y + oRow;

                                        if (boardX === otherX && boardY === otherY) {
                                            return false;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            return true;
        };

        const rotatePiece = (piece: SmartPiece) => {
            if (!config.rotationEnabled) return false;

            const rotations = tetrisPieces[piece.pieceType];
            const newRotationState = (piece.rotationState + 1) % rotations.length;
            const newShape = rotations[newRotationState];

            if (canPlacePiece(piece, piece.x, piece.y, newShape)) {
                piece.shape = newShape;
                piece.rotationState = newRotationState;
                return true;
            }
            return false;
        };

        const movePieceHorizontally = (piece: SmartPiece, direction: number) => {
            const newX = piece.x + direction;
            if (canPlacePiece(piece, newX, piece.y)) {
                piece.x = newX;
                return true;
            }
            return false;
        };

        const smartMovePiece = (piece: SmartPiece, now: number) => {
            // Only move if enough time has passed (prevent jerky movement)
            if (now - piece.lastMoveTime < 150) return;

            // Try to reach target rotation first
            if (piece.rotationState !== piece.targetRotation) {
                if (rotatePiece(piece)) {
                    piece.lastMoveTime = now;
                    return;
                }
            }

            // Try to reach target X position
            if (piece.x !== piece.targetX) {
                const direction = piece.targetX > piece.x ? 1 : -1;
                if (movePieceHorizontally(piece, direction)) {
                    piece.lastMoveTime = now;
                    return;
                } else {
                    // Can't reach target, pick a new one
                    piece.targetX = piece.x + Math.floor(Math.random() * 5) - 2;
                    piece.targetX = Math.max(0, Math.min(cols - 1, piece.targetX));
                }
            }
        };

        const settlePiece = (piece: SmartPiece) => {
            for (let row = 0; row < piece.shape.length; row++) {
                for (let col = 0; col < piece.shape[row].length; col++) {
                    if (piece.shape[row][col]) {
                        const boardX = piece.x + col;
                        const boardY = piece.y + row;

                        if (boardY >= 0 && boardY < rows && boardX >= 0 && boardX < cols) {
                            settledBlocks[boardY][boardX] = piece.color;
                        }
                    }
                }
            }
        };

        const clearFullLines = () => {
            for (let row = rows - 1; row >= 0; row--) {
                if (settledBlocks[row].every(cell => cell !== null)) {
                    // Clear this line
                    settledBlocks.splice(row, 1);
                    settledBlocks.unshift(Array(cols).fill(null));
                    row++; // Check the same row again
                }
            }
        };

        const checkGameOver = () => {
            // Check if blocks have stacked too high
            for (let col = 0; col < cols; col++) {
                if (settledBlocks[2] && settledBlocks[2][col]) { // If blocks reach near top
                    isGameOver = true;
                    gameOverHeight = Math.max(gameOverHeight, rows - 2);
                    break;
                }
            }

            // Game over effect - clear everything after a while
            if (isGameOver) {
                setTimeout(() => {
                    if (Math.random() < 0.3) { // 30% chance to reset
                        initializeGame();
                    }
                }, 3000);
            }
        };

        let animationFrameId: number;

        const gameLoop = (now: number) => {
            ctx.fillStyle = BRAND_COLORS.background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Spawn new pieces with configurable frequency
            const timeSinceLastSpawn = now - lastSpawnTime;
            const spawnInterval = config.spawnRate * 1000; // Convert to milliseconds

            if (timeSinceLastSpawn > spawnInterval && !isGameOver) {
                // Only spawn 1 piece at a time for smart gameplay
                if (activePieces.length < config.maxPieces) {
                    activePieces.push(createSmartPiece());
                }

                lastSpawnTime = now;
            }

            // Update active pieces
            activePieces = activePieces.filter(piece => {
                if (!piece.isActive) return false;

                // Smart movement logic
                smartMovePiece(piece, now);

                // Fall logic
                if (now - piece.lastFallTime > piece.fallSpeed) {
                    const newY = piece.y + 1;

                    if (canPlacePiece(piece, piece.x, newY)) {
                        piece.y = newY;
                    } else {
                        // Piece has landed - settle it
                        settlePiece(piece);
                        piece.isActive = false;
                        return false; // Remove from active pieces
                    }

                    piece.lastFallTime = now;
                }

                return piece.y < rows + 5; // Keep pieces slightly below screen
            });

            // Draw settled blocks
            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    const block = settledBlocks[row][col];
                    if (block) {
                        const opacity = (config.globalOpacity / 100);
                        const adjustedColor = block.replace(/70$/, Math.floor(0.7 * opacity * 255).toString(16).padStart(2, '0'));

                        ctx.fillStyle = adjustedColor;
                        ctx.fillRect(
                            col * blockSize,
                            row * blockSize,
                            blockSize - 2,
                            blockSize - 2
                        );

                        // Add slight border for depth
                        ctx.strokeStyle = `${adjustedColor.slice(0, -2)}40`;
                        ctx.lineWidth = 1;
                        ctx.strokeRect(
                            col * blockSize,
                            row * blockSize,
                            blockSize - 2,
                            blockSize - 2
                        );
                    }
                }
            }

            // Draw active pieces
            activePieces.forEach(piece => {
                if (!piece.isActive) return;

                for (let row = 0; row < piece.shape.length; row++) {
                    for (let col = 0; col < piece.shape[row].length; col++) {
                        if (piece.shape[row][col]) {
                            const x = (piece.x + col) * blockSize;
                            const y = (piece.y + row) * blockSize;

                            if (y >= 0) { // Only draw visible parts
                                const opacity = (config.globalOpacity / 100);
                                const adjustedColor = piece.color.replace(/70$/, Math.floor(0.7 * opacity * 255).toString(16).padStart(2, '0'));

                                ctx.fillStyle = adjustedColor;
                                ctx.fillRect(x, y, blockSize - 2, blockSize - 2);

                                // Add border
                                ctx.strokeStyle = `${adjustedColor.slice(0, -2)}40`;
                                ctx.lineWidth = 1;
                                ctx.strokeRect(x, y, blockSize - 2, blockSize - 2);
                            }
                        }
                    }
                }
            });

            // Clear full lines
            clearFullLines();

            // Check for game over
            checkGameOver();

            // Game over effect
            if (isGameOver) {
                const opacity = (config.globalOpacity / 100);
                ctx.fillStyle = `${BRAND_COLORS.primary}${Math.floor(0.2 * opacity * 255).toString(16).padStart(2, '0')}`;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }

            animationFrameId = requestAnimationFrame(gameLoop);
        };

        initializeGame();
        animationFrameId = requestAnimationFrame(gameLoop);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0"
            style={{ background: BRAND_COLORS.background }}
        />
    );
};
